<template>
  <!--
    We intentionally do NOT bind v-model here. UCalendar otherwise stamps
    its own saturated "selected day" background on whichever date is
    model-selected, which clashes with the custom soft band we render via
    the `#day` slot (the default background sits under our slot content).
    Selection state is fully driven by `isSelected` + `selectionBandClass`
    — `placeholderDate` tracks the visible month for navigation only.
  -->
  <UCalendar
    v-model:placeholder="placeholderDate"
    :week-starts-on="weekStartsOn"
    :locale="$i18n.locale"
    class="w-full"
    @update:placeholder="handlePlaceholderChange"
  >
    <template #day="{ day }">
      <div
        class="relative flex items-center justify-center w-8 h-8 cursor-pointer group"
        @click="handleDayClick(day)"
      >
        <!-- Hover background -->
        <div class="absolute inset-0 rounded-lg bg-gray-200/85 opacity-0 group-hover:opacity-100 transition-all dark:bg-gray-800" />

        <!--
          Selected-range band. Each selected day contributes a slice of a
          continuous bar: `rounded-l-lg` on the first day of a contiguous
          run, `rounded-r-lg` on the last, nothing in the middle. A tiny
          negative horizontal margin closes the grid-gap between adjacent
          days so the slices visually touch and read as one band per row.
        -->
        <div
          v-if="isSelected(day)"
          class="absolute inset-y-0 bg-primary-500/15 dark:bg-primary-500/25 transition-colors"
          :class="selectionBandClass(day)"
        />

        <!-- Day number -->
        <span
          :class="[
            'relative z-10 text-sm font-medium transition-all',
            isSelected(day)
              ? 'text-primary-700 dark:text-primary-300'
              : 'text-gray-700 dark:text-gray-200',
            isCurrentMonth(day) ? '' : 'opacity-60'
          ]"
        >
          {{ day.day }}
        </span>

        <!--
          Today indicator: small red dot pinned top-right so it doesn't
          compete with the bottom-aligned pressure/reservation indicators.
          Only "heavy" element in the calendar — everything else is soft.
        -->
        <span
          v-if="isToday(day)"
          class="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500 z-20"
          aria-hidden="true"
        />

        <!-- Pressure bar -->
        <div
          v-if="getDayStat(day)?.pressure"
          class="absolute -inset-x-4 bottom-0 px-1 z-30"
        >
          <div
            class="rounded-full h-1 mx-auto mb-0.5"
            :style="{
              width: Math.round(getDayStat(day)!.pressure! * 100) + '%',
              backgroundColor: getSchedulePressureColor(getDayStat(day)!.pressure!)
            }"
          />
        </div>

        <!-- Reservation dot (no pressure but has reservations) -->
        <div
          v-else-if="getDayStat(day)?.reservations"
          class="absolute inset-x-0 bottom-0 px-1 z-30"
        >
          <div class="rounded-full h-1 w-1 mx-auto mb-0.5 bg-primary" />
        </div>
      </div>
    </template>
  </UCalendar>
</template>

<script setup lang="ts">
import { CalendarDate, today as calendarToday, getLocalTimeZone, type DateValue } from '@internationalized/date'
import { getSchedulePressureColor } from '@/utils/schedule'

const { $api, $date, $dateFns, $auth, $bus } = useNuxtApp()
const instance = getCurrentInstance()

interface Props {
  modelValue: any[]
  options: any
  employee?: any
  employees?: any[] | null
}

const props = withDefaults(defineProps<Props>(), {
  employee: null,
  employees: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
}>()

interface DayStat {
  pressure?: number
  reservations?: number
}

const statsMap = ref<Map<string, DayStat>>(new Map())
const currentPage = ref<{ month: number; year: number } | null>(null)
let latestStatsRequest = 0

const todayDate = calendarToday(getLocalTimeZone())

// Convert YYYYMMDD string to CalendarDate
function toCalendarDate(dateStr: string): CalendarDate {
  const year = parseInt(dateStr.substring(0, 4))
  const month = parseInt(dateStr.substring(4, 6))
  const day = parseInt(dateStr.substring(6, 8))
  return new CalendarDate(year, month, day)
}

// Convert DateValue to YYYYMMDD string
function toDateString(date: DateValue): string {
  const y = String(date.year).padStart(4, '0')
  const m = String(date.month).padStart(2, '0')
  const d = String(date.day).padStart(2, '0')
  return `${y}${m}${d}`
}

// Visible-month anchor. Two-way bound via `v-model:placeholder` so UCalendar's
// navigation arrows keep it in sync. We seed it from the currently-selected
// range's first date so opening the calendar lands you on the right month.
const placeholderDate = ref<CalendarDate>(
  props.modelValue[0] ? toCalendarDate(props.modelValue[0]) : calendarToday(getLocalTimeZone()),
)

// Keep the visible month in sync if the selected range changes externally
// (e.g. date-control buttons navigating weeks).
watch(() => props.modelValue[0], (val) => {
  if (val) placeholderDate.value = toCalendarDate(val)
})

// Location's startDayOfWeek (1=Mon..7=Sun, StartDayOfWeek enum) is
// normalised by the composable to UCalendar's 0-6 scheme (Sun=0..Sat=6),
// which is also what JS `Date.getDay()` returns — so the same value
// works for both the calendar binding and the row-boundary math in
// `selectionBandClass`.
const weekStartsOn = useCalendarFirstDayOfWeek(() => $auth?.info?.location?.startDayOfWeek)

function isToday(day: DateValue): boolean {
  return day.year === todayDate.year && day.month === todayDate.month && day.day === todayDate.day
}

function isSelected(day: DateValue): boolean {
  const dayStr = toDateString(day)
  return props.modelValue.includes(dayStr)
}

/**
 * Tailwind classes for the selected-range band on `day`. Checks adjacent
 * selected days (and row boundaries) to decide whether this slice is the
 * start, middle, or end of a contiguous run — so a 7-day week renders as a
 * single horizontal band instead of 7 disconnected pills.
 *
 * Uses `-mx-1` to bleed across the calendar's grid gap; without it, even
 * adjacent slices would show a visible break.
 */
function selectionBandClass(day: DateValue): string {
  const dow = new Date(day.year, day.month - 1, day.day).getDay()
  const rowStart = weekStartsOn.value
  const rowEnd = (weekStartsOn.value + 6) % 7
  const isRowStart = dow === rowStart
  const isRowEnd = dow === rowEnd

  const prev = new Date(day.year, day.month - 1, day.day - 1)
  const next = new Date(day.year, day.month - 1, day.day + 1)
  const prevStr = `${prev.getFullYear()}${String(prev.getMonth() + 1).padStart(2, '0')}${String(prev.getDate()).padStart(2, '0')}`
  const nextStr = `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}${String(next.getDate()).padStart(2, '0')}`
  const prevSelected = !isRowStart && props.modelValue.includes(prevStr)
  const nextSelected = !isRowEnd && props.modelValue.includes(nextStr)

  // Isolated day — full pill (matches the day-view single-day case)
  if (!prevSelected && !nextSelected) return 'inset-x-0 rounded-lg'

  // Left edge of a contiguous run
  if (!prevSelected && nextSelected) return 'left-0 -right-1 rounded-l-lg'

  // Right edge of a contiguous run
  if (prevSelected && !nextSelected) return '-left-1 right-0 rounded-r-lg'

  // Middle — bleed to both sides to close the grid gap
  return '-inset-x-1'
}

function isCurrentMonth(day: DateValue): boolean {
  return day.year === placeholderDate.value.year && day.month === placeholderDate.value.month
}

function getDayStat(day: DateValue): DayStat | undefined {
  return statsMap.value.get(toDateString(day))
}

function handleDayClick(day: DateValue) {
  placeholderDate.value = new CalendarDate(day.year, day.month, day.day)

  // Emit the clicked date as a single-entry modelValue — the parent
  // Controller normalises this into its own 7-day (or N-day) range depending
  // on view mode and pushes the new date into the route.
  emit('update:modelValue', [toDateString(day)])
}

function handlePlaceholderChange(placeholder: DateValue) {
  currentPage.value = { month: placeholder.month, year: placeholder.year }
}

watch(currentPage, () => {
  if (currentPage.value) {
    getStats()
  }
}, { deep: true })

// Load stats on initial mount based on selected date
onMounted(() => {
  const val = props.modelValue[0]
  if (val) {
    const cd = toCalendarDate(val)
    currentPage.value = { month: cd.month, year: cd.year }
  }
})

const uid = instance?.uid
$bus.on.all('Reservation', uid, () => getStats())

onBeforeUnmount(() => {
  latestStatsRequest += 1
  $bus.off.all(uid)
})

async function getStats() {
  if (!currentPage.value) return

  const requestId = ++latestStatsRequest
  const { month, year } = currentPage.value
  const d = new Date(year, month - 1)
  const length = $dateFns.getDaysInMonth(d)
  const days: string[] = []

  let start = d

  while (days.length < length) {
    days.push($date.format(start))
    start = $date.add(start, { days: 1 })
  }

  const employees = props.employee ? [props.employee] : props.employees
  const employeeIds = (employees || []).map((e: any) => String(e.idEmployee))

  try {
    const response = await $api.booking.stats(days, employeeIds)

    if (requestId !== latestStatsRequest) return

    const dates = response.data?.dates

    if (!Array.isArray(dates)) {
      statsMap.value = new Map()
      return
    }

    const map = new Map<string, DayStat>()

    for (const stat of dates) {
      if (stat.pressure) {
        map.set(String(stat.date.value), { pressure: stat.pressure, reservations: stat.reservations })
      } else if (stat.reservations) {
        map.set(String(stat.date.value), { reservations: stat.reservations })
      }
    }

    statsMap.value = map
  } catch (e) {
    if (requestId !== latestStatsRequest) return

    console.log(e)
    statsMap.value = new Map()
  }
}
</script>
